import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWishlistSelectOptionBottomSheet } from 'goods/src/pages/GoodsDetail/components/SelectOptionBottomSheet/WishlistSelectOptionBottomSheet';
import type { SelectOptionConfirm } from 'goods/src/pages/GoodsDetail/components/SelectOptionBottomSheet/SelectOptionBottomSheet';
import { useWishlistPostGoodsActionBottomSheet } from 'goods/src/pages/GoodsDetail/components/PostGoodsActionBottomSheet/WishlistPostGoodsActionBottomSheet';
import { queryKey } from 'goods/src/pages/GoodsDetail/constant/queryKey';
import { bridge, isNotNil, isWeb } from 'shared/src/utils';
import {
  useBottomSheetRoute,
  useIsInFragmentScreens,
  useOpenDeeplink,
} from 'shared/src/hooks';
import { cartService } from 'shared/src/api';
import type { GoodsCtaButton } from 'shared/src/api/types';
import { useLogClickGoodsCtaButton } from './useLogClickGoodsCtaButton';
import type { LikedGoodsCardItem } from '../utils/convertLikedGoodsItemToGoodsData';

const SCREEN = 'LIKED_GOODS';

/**
 * 찜탭 상품카드 하단 CTA 버튼(장바구니 담기/예약하기) 탭 처리.
 * 버튼 렌더는 ui-kit GoodsCard 가 맡고, 여기서는 딥링크 해석·옵션시트·로깅만 다룬다.
 */
export const usePressGoodsCtaButton = () => {
  const queryClient = useQueryClient();
  const logClickGoodsCtaButton = useLogClickGoodsCtaButton();
  const { show: showWishlistOptionBottomSheet } =
    useWishlistSelectOptionBottomSheet();
  const { show: showWishlistPostGoodsActionBottomSheet } =
    useWishlistPostGoodsActionBottomSheet();
  const { open: openBottomSheetRoute } = useBottomSheetRoute();
  const isInFragmentScreens = useIsInFragmentScreens();
  const { openDeeplink } = useOpenDeeplink();
  const shownRecommendSnosRef = useRef(new Set<number>());

  const showCartRecommendOrToast = useCallback(
    async (entry: LikedGoodsCardItem) => {
      const sno = entry.item.sno;
      if (shownRecommendSnosRef.current.has(sno)) {
        bridge.toast('장바구니에 상품을 담았어요');
        return;
      }
      const recommend = await queryClient
        .fetchQuery({
          queryKey: queryKey.goodsCartRecommend(sno),
          queryFn: () => cartService.getAddCartRecommend(sno),
        })
        .catch(() => undefined);
      if ((recommend?.carousels.length ?? 0) === 0) {
        bridge.toast('장바구니에 상품을 담았어요');
        return;
      }
      shownRecommendSnosRef.current.add(sno);
      const landingDeeplink = isWeb
        ? 'ably://webview?url=https://mobile.a-bly.com/cart'
        : 'ably://webview?title_bar=false&url=cart%3Ffrom%3DRECOMMENDEDMODAL';
      await showWishlistPostGoodsActionBottomSheet({
        goodsId: sno,
        logging: {
          screen: SCREEN,
          folderSno: entry.item.like.folder_sno,
          marketSno: entry.item.market_sno,
          deliveryType: entry.item.delivery_type,
        },
        mode: 'cart',
        title: { highlight: '장바구니', text: '에 상품을 담았어요' },
        image: entry.item.image,
        action: { text: '바로가기', deeplink: landingDeeplink },
      });
    },
    [queryClient, showWishlistPostGoodsActionBottomSheet],
  );

  return useCallback(
    async (entry: LikedGoodsCardItem, ctaButton: GoodsCtaButton) => {
      logClickGoodsCtaButton(entry, ctaButton.click_event_name);
      if (ctaButton.action_type === 'DEEPLINK') {
        if (isNotNil(ctaButton.deeplink)) openDeeplink(ctaButton.deeplink);
        return;
      }
      const optionParams = {
        goodsId: entry.item.sno,
        mode: 'purchase' as const,
        isShowOnce: false,
        selectedOptions: [],
        updateSelectedOption: () => {},
        fromButton: `${SCREEN}_CARD_CART_BUTTON`,
        // PDP route 밖이라 옵션 시트가 PDP 상세 API 없이 카드 데이터로 로깅하도록 주입.
        logging: {
          screen: SCREEN,
          folderSno: entry.item.like.folder_sno,
          marketSno: entry.item.market_sno,
          deliveryType: entry.item.delivery_type,
        },
      };
      // 찜탭(LikeFragment)은 네이티브 탭바 호스팅이라 in-tree 바텀시트가 탭바에 가린다.
      // BottomSheetHost modal surface 경유로 띄워 탭바 위에 표시.
      const result = isInFragmentScreens
        ? await openBottomSheetRoute<typeof optionParams, SelectOptionConfirm>(
            'wishlist-select-option',
            optionParams,
          )
        : await showWishlistOptionBottomSheet(optionParams);
      if (result.type === 'confirm' && result.data.lastAction === 'addToCart') {
        await showCartRecommendOrToast(entry);
      }
    },
    [
      logClickGoodsCtaButton,
      openDeeplink,
      isInFragmentScreens,
      openBottomSheetRoute,
      showWishlistOptionBottomSheet,
      showCartRecommendOrToast,
    ],
  );
};
